/**
 * Realtor Property Details Service - Extracted API Calls
 * Follows SOLID, KISS, and YAGNI principles
 * Handles all property and user data operations for RieltorPropertyDetails component
 */

import { BaseService } from "./core/base-service";
import { httpClient } from "./core/http-client";
import type {
  IPropertyUser,
  IPropertyDetails,
} from "@/components/widgets/RealtorPropertyDetails/types";

// Enhanced type definitions for realtor property details
export interface PropertyDetailsResponse {
  success: boolean;
  data: IPropertyDetails;
  message?: string;
}

export interface UserDetailsResponse {
  success: boolean;
  data: IPropertyUser;
  message?: string;
}

// Request parameters
export interface PropertyDetailsParams {
  propertyId: string;
  dealType: string;
}

export interface UserDetailsParams {
  userId: string;
}

/**
 * Realtor Property Details Service Class
 * Handles property and user data operations for realtor interface
 * Single Responsibility: Property details and associated user data management
 */
class RealtorPropertyDetailsService extends BaseService {
  constructor() {
    super({
      name: "realtor-property-details",
      client: httpClient,
      enableLogging: true,
      retryAttempts: 3,
      retryDelay: 1000,
    });
  }

  /**
   * Fetch property details by ID
   * Extracted from RieltorPropertyDetails component
   * Enhanced with better error handling and response validation
   */
  async getPropertyDetails(
    params: PropertyDetailsParams,
  ): Promise<IPropertyDetails> {
    this.validateRequired(params, ["propertyId", "dealType"]);

    try {
      const response = await this.get<PropertyDetailsResponse>(
        `/property/property-info`,
        { propertyId: params.propertyId, dealType: params.dealType },
      );

      // Handle different response structures that might come from the API
      let propertyData: IPropertyDetails | null = null;

      if (response && typeof response === 'object') {
        // Try different possible response structures
        propertyData = response.data || response.property || (response as any);

        // If response has explicit success field, check it
        if ('success' in response && !response.success && !propertyData) {
          throw new Error(`Property not found: ${params.propertyId}`);
        }
      }

      // Final validation - ensure we have property data
      if (!propertyData || (typeof propertyData === 'object' && Object.keys(propertyData).length === 0)) {
        throw new Error(`Property not found or empty response: ${params.propertyId}`);
      }

      this.logOperationSuccess("get property details", {
        propertyId: params.propertyId,
        hasData: !!response.data
      });

      return propertyData;
    } catch (error) {
      // Enhanced error logging
      this.logger.error("Failed to fetch property details", {
        propertyId: params.propertyId,
        dealType: params.dealType,
        error: error instanceof Error ? error.message : String(error)
      });

      this.handleError(error, "get property details");
      throw error; // Re-throw to maintain error handling in component
    }
  }

  /**
   * Fetch user details by ID
   * Extracted from RieltorPropertyDetails component for creator/responsible user data
   */
  async getUserDetails(params: UserDetailsParams): Promise<IPropertyUser> {
    this.validateRequired(params, ["userId"]);

    try {
      const response = await this.get<UserDetailsResponse>(`/user/user-info`, {
        userId: params.userId,
      });

      if (!response.success || !response.data) {
        this.handleError(
          {
            message: "User not found or invalid response",
            code: "USER_NOT_FOUND",
            details: { userId: params.userId },
          },
          "get user details",
        );
      }

      this.logOperationSuccess("get user details", {
        userId: params.userId,
        hasData: !!response.data,
      });

      return response.data || response;
    } catch (error) {
      this.handleError(error, "get user details");
      throw error; // Re-throw to maintain error handling in component
    }
  }

  /**
   * Fetch creator user details
   * Convenience method for getting property creator information
   */
  async getCreatorDetails(createdBy: string): Promise<IPropertyUser> {
    return this.getUserDetails({ userId: createdBy });
  }

  /**
   * Fetch responsible user details
   * Convenience method for getting responsible person information
   */
  async getResponsibleUserDetails(responsible: string): Promise<IPropertyUser> {
    return this.getUserDetails({ userId: responsible });
  }

  /**
   * Batch fetch multiple users
   * Optimized method to fetch creator and responsible user in parallel
   */
  async batchGetUsers(
    userIds: string[],
  ): Promise<Record<string, IPropertyUser>> {
    if (!userIds || userIds.length === 0) {
      return {};
    }

    try {
      const userPromises = userIds.filter(Boolean).map(async (userId) => {
        try {
          const user = await this.getUserDetails({ userId });
          return { userId, user };
        } catch (error) {
          this.logger.warn(`Failed to fetch user ${userId}`, { error });
          return { userId, user: null };
        }
      });

      const results = await Promise.allSettled(userPromises);
      const users: Record<string, IPropertyUser> = {};

      results.forEach((result, index) => {
        if (result.status === "fulfilled" && result.value.user) {
          users[result.value.userId] = result.value.user;
        }
      });

      this.logOperationSuccess("batch get users", {
        requestedCount: userIds.length,
        fetchedCount: Object.keys(users).length,
      });

      return users;
    } catch (error) {
      this.handleError(error, "batch get users");
      return {};
    }
  }

  /**
   * Get property with associated user data
   * Complete method that fetches property and associated users in one call
   */
  async getPropertyWithUsers(
    propertyId: string,
    dealType: string,
  ): Promise<{
    property: IPropertyDetails;
    creator: IPropertyUser | null;
    responsible: IPropertyUser | null;
  }> {
    try {
      // First get property details
      const property = await this.getPropertyDetails({ propertyId, dealType });

      // Extract user IDs
      const userIds = [
        property.creatorFields?.createdBy,
        property.creatorFields?.responsible,
      ].filter(Boolean) as string[];

      // Batch fetch users
      const users = await this.batchGetUsers(userIds);

      const result = {
        property,
        creator: property.creatorFields?.createdBy
          ? users[property.creatorFields.createdBy] || null
          : null,
        responsible: property.creatorFields?.responsible
          ? users[property.creatorFields.responsible] || null
          : null,
      };

      this.logOperationSuccess("get property with users", {
        propertyId,
        hasCreator: !!result.creator,
        hasResponsible: !!result.responsible,
      });

      return result;
    } catch (error) {
      this.handleError(error, "get property with users");
      throw error;
    }
  }

  /**
   * Enhanced validation for property details parameters
   */
  protected validateRequired(
    params: Record<string, any>,
    requiredFields: string[],
  ): void {
    // Run base validation
    super.validateRequired(params, requiredFields);

    // Additional validation for property/user IDs
    if (params.propertyId && typeof params.propertyId !== "string") {
      this.handleError(
        {
          message: "Property ID must be a string",
          code: "INVALID_PROPERTY_ID",
          details: { propertyId: params.propertyId },
        },
        "property ID validation",
      );
    }

    if (params.userId && typeof params.userId !== "string") {
      this.handleError(
        {
          message: "User ID must be a string",
          code: "INVALID_USER_ID",
          details: { userId: params.userId },
        },
        "user ID validation",
      );
    }
  }

  /**
   * Custom health check for realtor property details service
   */
  async healthCheck() {
    try {
      // Simple health check - just verify the service is responsive
      return {
        service: this.serviceName,
        status: "healthy" as const,
        timestamp: new Date().toISOString(),
        endpoints: {
          propertyDetails: "healthy",
          userDetails: "healthy",
          batchUsers: "healthy",
        },
      };
    } catch (error) {
      return {
        service: this.serviceName,
        status: "unhealthy" as const,
        timestamp: new Date().toISOString(),
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  }
}

// Export singleton instance
export const realtorPropertyDetailsService =
  new RealtorPropertyDetailsService();

// Export for backward compatibility
export default realtorPropertyDetailsService;

// Export types for external use
export type {
  PropertyDetailsResponse,
  UserDetailsResponse,
  PropertyDetailsParams,
  UserDetailsParams,
};
